import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiEndpoint } from "../../utils/env";
import {
  cancelUploadSession,
  createUploadSession,
  listUploadSessions,
  registerUploadSession,
  uploadSessionFiles,
} from "../../api/uploadSessionApi";

const FILE_TYPES = ["AFP", "PDF", "POSTSCRIPT", "HTML", "ANY"];
const STATUS_FILTERS = ["", "Open", "Uploaded", "Registered", "Cancelled", "Expired", "Consumed"];

function fmtBytes(n) {
  if (n == null) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = Number(n);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function fmtDate(s) {
  if (!s) return "-";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

/**
 * Bulk upload sessions section — rendered inside Upload.jsx when section === "sessions".
 * Self-contained: lists sessions, creates new ones, accepts file uploads for LocalFolder mode,
 * finalizes via register, and cancels. No admin-only surface — org-scoped by the API.
 */
export default function BulkSessionsSection({ token }) {
  const { t } = useTranslation();

  const [sessions, setSessions] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    scope: "Session",
    ttlMinutes: 60,
    estimatedDocumentCount: "",
    bytesExpected: "",
    notes: "",
  });
  const [createBusy, setCreateBusy] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [createAdvancedOpen, setCreateAdvancedOpen] = useState(false);

  // Active session (for upload / register flows)
  const [activeSession, setActiveSession] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const filePickerRef = useRef(null);

  // Register modal
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    databaseName: "",
    fileType: "AFP",
    archiveFileName: "",
    indexFileName: "",
  });
  const [registerBusy, setRegisterBusy] = useState(false);

  // Keep both display name (for UI) and physical dbName (for register payload)
  const dbOptions = useMemo(
    () => (Array.isArray(databases)
      ? databases.map(d => ({
          id: d.id,
          dbName: d.dbName,
          label: d.displayName || d.dbName,
        }))
      : []),
    [databases]
  );

  // ── Loaders ────────────────────────────────────────────────────────────────

  const loadDatabases = useCallback(async () => {
    try {
      const res = await fetch(apiEndpoint("/api/Upload/AllDatabases"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("no-endpoint");
      const data = await res.json();
      setDatabases(Array.isArray(data) ? data : []);
    } catch {
      setDatabases([]);
    }
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listUploadSessions(token, { status: filterStatus || undefined, take: 100 });
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Failed to load sessions.");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [token, filterStatus]);

  useEffect(() => { load(); loadDatabases(); }, [load, loadDatabases]);

  // ── Create ─────────────────────────────────────────────────────────────────

  const openCreate = () => {
    setCreateForm({
      scope: "Session",
      ttlMinutes: 60,
      estimatedDocumentCount: "",
      bytesExpected: "",
      notes: "",
    });
    setCreateResult(null);
    setCreateAdvancedOpen(false);
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    setCreateBusy(true);
    setError("");
    try {
      const payload = {
        scope: createForm.scope || "Session",
        ttlMinutes: Number(createForm.ttlMinutes) || undefined,
        estimatedDocumentCount: createForm.estimatedDocumentCount
          ? Number(createForm.estimatedDocumentCount) : undefined,
        bytesExpected: createForm.bytesExpected
          ? Number(createForm.bytesExpected) : undefined,
        notes: createForm.notes || undefined,
      };
      const result = await createUploadSession(token, payload);
      setCreateResult(result);
      setActiveSession(result);
      await load();
    } catch (e) {
      setError(e.message || "Failed to create session.");
    } finally {
      setCreateBusy(false);
    }
  };

  // ── Upload files ───────────────────────────────────────────────────────────

  const handleFileDrop = async (files) => {
    if (!activeSession?.sessionGuid) return;
    if (!files || files.length === 0) return;
    setUploadBusy(true);
    setError("");
    try {
      const result = await uploadSessionFiles(token, activeSession.sessionGuid, files);
      setUploadResult(result);
      if (result?.files?.length) {
        setRegisterForm(f => ({
          ...f,
          archiveFileName: f.archiveFileName || result.files[0].name,
          indexFileName:
            f.indexFileName ||
            (result.files[1]?.name && /\.(jrn|idx)$/i.test(result.files[1].name)
              ? result.files[1].name : (f.indexFileName || "")),
        }));
      }
      await load();
    } catch (e) {
      setError(e.message || "Upload failed.");
    } finally {
      setUploadBusy(false);
    }
  };

  // ── Register ───────────────────────────────────────────────────────────────

  const openRegister = (session) => {
    setActiveSession(session);
    setRegisterForm({
      databaseName: dbOptions[0]?.dbName || "",
      fileType: "AFP",
      archiveFileName: uploadResult?.files?.[0]?.name || "",
      indexFileName: "",
    });
    setRegisterOpen(true);
  };

  const submitRegister = async () => {
    if (!activeSession?.sessionGuid) return;
    setRegisterBusy(true);
    setError("");
    try {
      const res = await registerUploadSession(token, activeSession.sessionGuid, registerForm);
      setRegisterOpen(false);
      setActiveSession(null);
      setUploadResult(null);
      setCreateResult(null);
      alert(t("upload.sessions.registerSuccess", {
        batchId: res.batchProcessId,
        defaultValue: `Session registered — BatchProcess #${res.batchProcessId}`,
      }));
      await load();
    } catch (e) {
      setError(e.message || "Register failed.");
    } finally {
      setRegisterBusy(false);
    }
  };

  // ── Cancel ─────────────────────────────────────────────────────────────────

  const handleCancel = async (session) => {
    const ok = window.confirm(t("upload.sessions.confirmCancel", {
      guid: session.sessionGuid,
      defaultValue: `Cancel upload session ${session.sessionGuid}?`,
    }));
    if (!ok) return;
    try {
      await cancelUploadSession(token, session.sessionGuid);
      if (activeSession?.sessionGuid === session.sessionGuid) {
        setActiveSession(null);
        setCreateResult(null);
        setUploadResult(null);
      }
      await load();
    } catch (e) {
      setError(e.message || "Cancel failed.");
    }
  };

  // ── Status → badge variant ─────────────────────────────────────────────────

  const badgeClass = (status) => {
    switch (status) {
      case "Open":       return "df-badge df-badge-warning";
      case "Uploaded":   return "df-badge df-badge-info";
      case "Registered": return "df-badge df-badge-active";
      case "Consumed":   return "df-badge df-badge-active";
      case "Cancelled":  return "df-badge df-badge-neutral";
      case "Expired":    return "df-badge df-badge-error";
      default:           return "df-badge df-badge-neutral";
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="df-panel">
        {/* Header — mirrors Drop folders header */}
        <div className="df-header">
          <div className="df-title">
            {t("upload.sessions.title", { defaultValue: "Bulk upload sessions" })}
          </div>
          <div className="df-stats-pill">
            {sessions.length}
            {filterStatus ? ` · ${filterStatus}` : ""}
          </div>
          <select
            className="df-status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            title={t("upload.sessions.filterStatus", { defaultValue: "Filter by status" })}
          >
            {STATUS_FILTERS.map(s => (
              <option key={s || "all"} value={s}>
                {s || t("upload.sessions.statusAll", { defaultValue: "All statuses" })}
              </option>
            ))}
          </select>
          <button className="df-refresh-btn" title={t("upload.sessions.refresh", { defaultValue: "Refresh" })} onClick={load} disabled={loading}>
            ⟲
          </button>
          <button className="btn-setup" onClick={openCreate}>
            ＋ {t("upload.sessions.newSession", { defaultValue: "New session" })}
          </button>
        </div>

        {/* Description */}
        <div className="bs-description">
          {t("upload.sessions.description", {
            defaultValue: "Create a session to receive a temporary upload location, upload one or more archive files, then register the batch for processing.",
          })}
        </div>

        {error && <div className="bs-error-banner">{error}</div>}

        {/* Column headings */}
        <div className="df-thead df-thead--sessions">
          <div>{t("upload.sessions.colGuid", { defaultValue: "Session" })}</div>
          <div>{t("upload.sessions.colStatus", { defaultValue: "Status" })}</div>
          <div>{t("upload.sessions.colScope", { defaultValue: "Scope" })} · {t("upload.sessions.colStorage", { defaultValue: "Storage" })}</div>
          <div>{t("upload.sessions.colBytes", { defaultValue: "Received" })}</div>
          <div>{t("upload.sessions.colBatchId", { defaultValue: "Batch" })}</div>
          <div>{t("upload.sessions.colCreated", { defaultValue: "Created" })}</div>
          <div style={{ textAlign: "right" }}>{t("upload.sessions.colActions", { defaultValue: "Actions" })}</div>
        </div>

        {/* Rows */}
        {sessions.length === 0 && !loading && (
          <div className="df-empty">
            <div className="df-empty-icon">📦</div>
            <div className="df-empty-text">
              {t("upload.sessions.empty", { defaultValue: "No upload sessions yet." })}
            </div>
          </div>
        )}

        {sessions.map(s => {
          const active = s.status === "Open" || s.status === "Uploaded";
          return (
            <div
              key={s.sessionGuid}
              className={`df-row df-row--sessions ${active ? "df-configured" : "df-unconfigured"}`}
            >
              <div className="df-mono" title={s.sessionGuid}>
                {s.sessionGuid.substring(0, 10)}…
              </div>
              <div>
                <span className={badgeClass(s.status)}>{s.status}</span>
              </div>
              <div className="df-path-cell">
                {s.scope} · <span className="df-subtle">{s.storageMode}</span>
              </div>
              <div className="df-subtle">{fmtBytes(s.bytesReceived)}</div>
              <div className="df-subtle">{s.batchProcessId ?? "—"}</div>
              <div className="df-last-updated" title={`${t("upload.sessions.colExpires", { defaultValue: "Expires" })}: ${fmtDate(s.expiresAt)}`}>
                {fmtDate(s.createdAt)}
              </div>
              <div className="df-actions-cell">
                {active && s.storageMode === "LocalFolder" && (
                  <button
                    className="btn-edit"
                    onClick={() => { setActiveSession(s); setUploadResult(null); setCreateResult(null); }}
                  >
                    {t("upload.sessions.actionUpload", { defaultValue: "Upload" })}
                  </button>
                )}
                {active && (
                  <button className="btn-setup" onClick={() => openRegister(s)}>
                    {t("upload.sessions.actionRegister", { defaultValue: "Register" })}
                  </button>
                )}
                {active && (
                  <button className="btn-delete" onClick={() => handleCancel(s)}>
                    {t("upload.sessions.actionCancel", { defaultValue: "Cancel" })}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Active session upload pane ─────────────────────────────────────── */}
      {activeSession && (activeSession.status === "Open" || activeSession.status === "Uploaded") && activeSession.storageMode === "LocalFolder" && (
        <div className="df-panel" style={{ marginTop: 16 }}>
          <div className="bs-active-pane">
            <h3>
              {t("upload.sessions.activeHeader", {
                guid: activeSession.sessionGuid.substring(0, 10),
                defaultValue: `Session ${activeSession.sessionGuid.substring(0, 10)}…`,
              })}
            </h3>
            <div className="df-mono bs-active-path">{activeSession.localPath}</div>
            <div
              className={`bs-dropzone ${uploadBusy ? "is-busy" : ""}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const files = Array.from(e.dataTransfer.files || []);
                handleFileDrop(files);
              }}
              onClick={() => filePickerRef.current?.click()}
            >
              {uploadBusy
                ? t("upload.sessions.uploading", { defaultValue: "Uploading…" })
                : t("upload.sessions.dropOrClick", { defaultValue: "Drop files here or click to choose" })}
              <input
                ref={filePickerRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFileDrop(Array.from(e.target.files || []))}
              />
            </div>
            {uploadResult && uploadResult.files && (
              <div className="bs-uploaded-list">
                <strong>{t("upload.sessions.uploadedHeader", { defaultValue: "Uploaded" })}</strong>
                <ul>
                  {uploadResult.files.map((f, i) => (
                    <li key={i}><code>{f.name}</code> ({fmtBytes(f.size)})</li>
                  ))}
                </ul>
                <button className="btn-setup" onClick={() => openRegister(activeSession)}>
                  {t("upload.sessions.actionRegister", { defaultValue: "Register" })} →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Create modal ───────────────────────────────────────────────────── */}
      {createOpen && (
        <div className="modal-overlay" onClick={() => !createBusy && setCreateOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              <span className="modal-title-icon">📦</span>
              <div className="modal-title-content">
                <div className="modal-title-text">
                  {!createResult
                    ? t("upload.sessions.createTitle", { defaultValue: "New upload session" })
                    : t("upload.sessions.createdOk", { defaultValue: "Session created" })}
                </div>
                <div className="modal-title-sub">
                  {!createResult
                    ? t("upload.sessions.createSub", {
                        defaultValue: "Create a temporary upload location for a batch of archive files.",
                      })
                    : t("upload.sessions.createdSub", {
                        defaultValue: "Your session is ready — upload files next.",
                      })}
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => !createBusy && setCreateOpen(false)} />
            </div>

            {!createResult ? (
              <>
                <div className="modal-body">
                  <div className="form-row">
                    <label>{t("upload.sessions.fieldTtl", { defaultValue: "Keep open for" })}</label>
                    <select
                      value={String(createForm.ttlMinutes)}
                      onChange={(e) => setCreateForm({ ...createForm, ttlMinutes: e.target.value })}
                    >
                      <option value="60">{t("upload.sessions.ttl1h", { defaultValue: "1 hour" })}</option>
                      <option value="240">{t("upload.sessions.ttl4h", { defaultValue: "4 hours" })}</option>
                      <option value="1440">{t("upload.sessions.ttl1d", { defaultValue: "1 day" })}</option>
                      <option value="4320">{t("upload.sessions.ttl3d", { defaultValue: "3 days" })}</option>
                    </select>
                    <div className="field-hint">
                      {t("upload.sessions.ttlHint", {
                        defaultValue: "How long the upload location will accept files before it expires.",
                      })}
                    </div>
                  </div>

                  <div className="form-row">
                    <label>{t("upload.sessions.fieldEstimatedDocs", { defaultValue: "Document count" })}</label>
                    <input
                      type="number" min="0" placeholder="e.g. 1500"
                      value={createForm.estimatedDocumentCount}
                      onChange={(e) => setCreateForm({ ...createForm, estimatedDocumentCount: e.target.value })}
                    />
                    <div className="field-hint">
                      {t("upload.sessions.estimatedDocsHint", {
                        defaultValue: "Optional. Approximate number of documents in this batch. Used to pre-check your license allowance.",
                      })}
                    </div>
                  </div>

                  <div className="form-row">
                    <label>{t("upload.sessions.fieldNotes", { defaultValue: "Notes" })}</label>
                    <input
                      type="text" maxLength="2000"
                      placeholder={t("upload.sessions.notesPlaceholder", { defaultValue: "e.g. February statements" })}
                      value={createForm.notes}
                      onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                    />
                    <div className="field-hint">
                      {t("upload.sessions.notesHint", {
                        defaultValue: "Optional. A short description to help you identify this batch later.",
                      })}
                    </div>
                  </div>

                  <div className="advanced-section">
                    <div className="advanced-divider" />
                    <button
                      type="button"
                      className="advanced-toggle"
                      onClick={() => setCreateAdvancedOpen(!createAdvancedOpen)}
                    >
                      <span>⚙ {t("upload.sessions.advanced", { defaultValue: "Advanced settings" })}</span>
                      <span className={`adv-chevron${createAdvancedOpen ? " open" : ""}`}>▾</span>
                    </button>
                    {createAdvancedOpen && (
                      <div className="advanced-body">
                        <div className="form-row">
                          <label>{t("upload.sessions.fieldScope", { defaultValue: "Upload type" })}</label>
                          <select
                            value={createForm.scope}
                            onChange={(e) => setCreateForm({ ...createForm, scope: e.target.value })}
                          >
                            <option value="Session">
                              {t("upload.sessions.scopeSessionLabel", { defaultValue: "One-time batch (disposable)" })}
                            </option>
                            <option value="CustomerFixed">
                              {t("upload.sessions.scopeCustomerLabel", { defaultValue: "Reuse organisation folder (recurring)" })}
                            </option>
                          </select>
                          <div className="field-hint">
                            {t("upload.sessions.scopeHint", {
                              defaultValue: "One-time creates a fresh location just for this upload. Reuse-organisation is for customers who upload regularly and want a stable endpoint.",
                            })}
                          </div>
                        </div>

                        <div className="form-row">
                          <label>{t("upload.sessions.fieldBytesExpected", { defaultValue: "Total size" })}</label>
                          <input
                            type="number" min="0" placeholder="bytes"
                            value={createForm.bytesExpected}
                            onChange={(e) => setCreateForm({ ...createForm, bytesExpected: e.target.value })}
                          />
                          <div className="field-hint">
                            {t("upload.sessions.bytesHint", {
                              defaultValue: "Optional, in bytes. Used for progress estimates only.",
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-actions">
                  <button className="secondary-btn" onClick={() => setCreateOpen(false)} disabled={createBusy}>
                    {t("common.cancel", { defaultValue: "Cancel" })}
                  </button>
                  <button className="primary-btn" onClick={submitCreate} disabled={createBusy}>
                    {createBusy
                      ? t("upload.sessions.creating", { defaultValue: "Creating…" })
                      : t("upload.sessions.create", { defaultValue: "Create upload session" })}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-body">
                  <div className="bs-created-card">
                    <div className="bs-created-title">
                      <span>✓</span>
                      {t("upload.sessions.createdOk", { defaultValue: "Session created" })}
                    </div>

                    <div className="bs-created-row">
                      <div className="bs-created-label">
                        {t("upload.sessions.colGuid", { defaultValue: "Session" })}
                      </div>
                      <div className="bs-created-value">{createResult.sessionGuid}</div>
                    </div>

                    {createResult.storageMode === "LocalFolder" && createResult.localPath && (
                      <div className="bs-created-row">
                        <div className="bs-created-label">
                          {t("upload.sessions.localPath", { defaultValue: "Local path" })}
                        </div>
                        <div className="bs-created-value">{createResult.localPath}</div>
                      </div>
                    )}

                    {createResult.uploadUrl && (
                      <div className="bs-created-row">
                        <div className="bs-created-label">
                          {t("upload.sessions.uploadUrl", { defaultValue: "Upload URL" })}
                        </div>
                        <div className="bs-created-value">{createResult.uploadUrl}</div>
                      </div>
                    )}

                    <div className="bs-created-row">
                      <div className="bs-created-label">
                        {t("upload.sessions.colExpires", { defaultValue: "Expires" })}
                      </div>
                      <div className="bs-created-value">{fmtDate(createResult.expiresAt)}</div>
                    </div>
                  </div>

                  <div className="field-hint" style={{ gridColumn: "1 / -1", marginTop: 0 }}>
                    {t("upload.sessions.createdNextStep", {
                      defaultValue: "Next: close this dialog and click Upload on the new session row to drop your files in.",
                    })}
                  </div>
                </div>

                <div className="modal-actions">
                  <button className="primary-btn" onClick={() => setCreateOpen(false)}>
                    {t("upload.sessions.continue", { defaultValue: "Continue" })}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Register modal ─────────────────────────────────────────────────── */}
      {registerOpen && activeSession && (
        <div className="modal-overlay" onClick={() => !registerBusy && setRegisterOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              <span className="modal-title-icon">📝</span>
              <div className="modal-title-content">
                <div className="modal-title-text">
                  {t("upload.sessions.registerTitle", { defaultValue: "Register for processing" })}
                </div>
                <div className="modal-title-sub">
                  {t("upload.sessions.registerSub", {
                    defaultValue: "The uploaded files will be queued for the worker to process.",
                  })}
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => !registerBusy && setRegisterOpen(false)} />
            </div>

            <div className="modal-body">
              <div className="form-row">
                <label>
                  {t("upload.sessions.fieldDatabase", { defaultValue: "Target database" })}{" "}
                  <span className="required-mark">*</span>
                </label>
                {dbOptions.length > 0 ? (
                  <select
                    value={registerForm.databaseName}
                    onChange={(e) => setRegisterForm({ ...registerForm, databaseName: e.target.value })}
                  >
                    <option value="" disabled>
                      {t("upload.sessions.selectDatabase", { defaultValue: "Select a database…" })}
                    </option>
                    {dbOptions.map(d => <option key={d.id} value={d.dbName}>{d.label}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder={t("upload.sessions.databasePlaceholder", { defaultValue: "Type database name" })}
                    value={registerForm.databaseName}
                    onChange={(e) => setRegisterForm({ ...registerForm, databaseName: e.target.value })}
                  />
                )}
                <div className="field-hint">
                  {t("upload.sessions.databaseHint", {
                    defaultValue: "Which archive database should these documents be ingested into.",
                  })}
                </div>
              </div>

              <div className="form-row">
                <label>
                  {t("upload.sessions.fieldFileType", { defaultValue: "Document format" })}{" "}
                  <span className="required-mark">*</span>
                </label>
                <div className="bs-type-pills">
                  {FILE_TYPES.map(ft => (
                    <div
                      key={ft}
                      className={`bs-type-pill ${registerForm.fileType === ft ? "is-active" : ""}`}
                      onClick={() => setRegisterForm({ ...registerForm, fileType: ft })}
                    >
                      {ft}
                    </div>
                  ))}
                </div>
                <div className="field-hint">
                  {t("upload.sessions.fileTypeHint", {
                    defaultValue: "The format of your archive file. AFP for IBM mainframe output, PDF for bulk PDF archives, etc.",
                  })}
                </div>
              </div>

              <div className="form-row">
                <label>
                  {t("upload.sessions.fieldArchiveName", { defaultValue: "Archive file" })}{" "}
                  <span className="required-mark">*</span>
                </label>
                <input
                  type="text"
                  placeholder="archive.afp"
                  value={registerForm.archiveFileName}
                  onChange={(e) => setRegisterForm({ ...registerForm, archiveFileName: e.target.value })}
                />
                <div className="field-hint">
                  {t("upload.sessions.archiveHint", {
                    defaultValue: "The main data file in your upload. Usually pre-filled from the last upload.",
                  })}
                </div>
              </div>

              <div className="form-row">
                <label>{t("upload.sessions.fieldIndexName", { defaultValue: "Index file" })}</label>
                <input
                  type="text"
                  placeholder={t("upload.optionalLabel", { defaultValue: "optional" })}
                  value={registerForm.indexFileName}
                  onChange={(e) => setRegisterForm({ ...registerForm, indexFileName: e.target.value })}
                />
                <div className="field-hint">
                  {t("upload.sessions.indexHint", {
                    defaultValue: "Optional. Journal or index file (e.g. .jrn / .idx) describing where each document starts in the archive.",
                  })}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="secondary-btn" onClick={() => setRegisterOpen(false)} disabled={registerBusy}>
                {t("common.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                className="primary-btn"
                onClick={submitRegister}
                disabled={registerBusy || !registerForm.databaseName || !registerForm.archiveFileName}
              >
                {registerBusy
                  ? t("upload.sessions.registering", { defaultValue: "Registering…" })
                  : t("upload.sessions.doRegister", { defaultValue: "Register for processing" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
